import React, { useState } from 'react';

interface PromptDialogProps {
  isOpen: boolean;
  title: string;
  message?: string;
  placeholder?: string;
  confirmText?: string;
  cancelText?: string;
  type?: 'info' | 'warning';
  defaultValue?: string;
  onConfirm: (value: string) => void;
  onCancel: () => void;
}

export const PromptDialog: React.FC<PromptDialogProps> = ({
  isOpen,
  title,
  message,
  placeholder = '',
  confirmText = 'Aceptar',
  cancelText = 'Cancelar',
  type = 'info',
  defaultValue = '',
  onConfirm,
  onCancel
}) => {
  const [value, setValue] = useState(defaultValue);

  if (!isOpen) return null;

  const typeStyles = {
    info: {
      bg: 'bg-blue-50 dark:bg-blue-900/20',
      border: 'border-blue-200 dark:border-blue-800',
      icon: 'edit',
      iconColor: 'text-blue-500'
    },
    warning: {
      bg: 'bg-yellow-50 dark:bg-yellow-900/20',
      border: 'border-yellow-200 dark:border-yellow-800',
      icon: 'warning',
      iconColor: 'text-yellow-500'
    }
  };

  const styles = typeStyles[type];

  const handleConfirm = () => {
    onConfirm(value);
    setValue('');
  };

  const handleCancel = () => {
    onCancel();
    setValue('');
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in">
      <div className="bg-white dark:bg-surface-dark rounded-2xl max-w-md w-full shadow-2xl animate-slide-up">
        <div className={`${styles.bg} p-6 rounded-t-2xl border-b ${styles.border}`}>
          <div className="flex items-center gap-3">
            <span className={`material-symbols-outlined text-2xl ${styles.iconColor}`}>
              {styles.icon}
            </span>
            <h3 className="text-lg font-bold text-text-main dark:text-white">{title}</h3>
          </div>
        </div>

        <div className="p-6 space-y-4">
          {message && (
            <p className="text-text-main dark:text-white leading-relaxed">{message}</p>
          )}
          <input
            type="text"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={placeholder}
            className="w-full px-4 py-3 bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-xl text-text-main dark:text-white focus:outline-none focus:ring-2 focus:ring-primary"
            autoFocus
          />
        </div>

        <div className="flex gap-3 p-6 pt-0">
          <button
            onClick={handleCancel}
            className="flex-1 px-4 py-3 bg-gray-100 dark:bg-white/10 text-text-main dark:text-white font-semibold rounded-xl hover:bg-gray-200 dark:hover:bg-white/20 transition-colors"
          >
            {cancelText}
          </button>
          <button
            onClick={handleConfirm}
            className="flex-1 px-4 py-3 bg-primary text-white font-semibold rounded-xl hover:bg-orange-600 transition-colors"
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PromptDialog;
